import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, InjectionToken, NgZone, Renderer2, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HammerGestureConfig } from '@angular/platform-browser';
import { NavigationStart, Router } from '@angular/router';

import { AppConfig } from '@frontend/sports/common/client-config-data-access';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { DeviceService, isViewTransitionsEnabled } from '@frontend/vanilla/core';
import fastdom from 'fastdom';
import { TouchMouseInput } from 'hammerjs';
import { delay, filter, fromEvent, merge } from 'rxjs';

import { GestureOptions, PopupAction } from '../../common/modal-window.actions';
import { ModalGestureAnimationService } from './modal-gesture-animation.service';
import { ModalGestureBackgroundService } from './modal-gesture-background.service';
import { ModalGestureHelperService } from './modal-gesture-helper.service';
import { ModalGestureIsClosingService } from './modal-gesture-is-closing.service';
import { ModalGesturePanUtility } from './modal-gesture-pan-utility';
import { ModalGestureQueriesService } from './modal-gesture-queries.service';
import { ModalGestureTransformOriginService } from './modal-gesture-transform-origin.service';
import {
    MAX_BACKGROUND_BRIGHTNESS,
    MAX_BACKGROUND_SCALE,
    MIN_BACKGROUND_BRIGHTNESS,
    MIN_BACKGROUND_SCALE,
    PANEND_CLOSE_DURATION_VERTICAL,
    PANEND_CLOSE_EASING_VERTICAL,
    PANEND_REOPEN_DURATION_VERTICAL,
    PANEND_REOPEN_EASING_VERTICAL,
    PAN_BUFFER,
    PAN_THRESHOLD,
    TRIGGER_PANEND_AFTER_TOUCHEND_MS,
} from './modal-gesture.constants';
import { Background } from './modal-gesture.model';

export const VERTICAL_GESTURE_CONFIG = new InjectionToken<HammerGestureConfig>('ModalGestureVerticalConfig');
@Injectable()
export class ModalGestureVerticalConfig extends HammerGestureConfig {
    override overrides = {
        pan: {
            threshold: PAN_THRESHOLD,
            direction: Hammer.DIRECTION_VERTICAL,
        },
    };

    override options = {
        inputClass: TouchMouseInput,
    };
}

@Injectable()
export class ModalVerticalGestureService {
    #isDragging = false;
    #isAnimating = false;
    #isPanUpdateScheduled = false;
    #isBackgroundZoomable = true;
    #currentZoom = 0;
    #currentPosition = 0;
    #currentBrightness = 0;
    #modalWindow: HTMLElement;
    #modalContent: HTMLElement | null;
    #background: Background;
    #headerSelector?: string;
    #headerHeight?: number;
    #scrollContainerSelector?: string;
    #scrollContainer?: HTMLElement | null;

    readonly #ngZone = inject(NgZone);
    readonly #document = inject(DOCUMENT);
    readonly #destroyRef = inject(DestroyRef);
    readonly #renderer = inject(Renderer2);
    readonly #appConfig = inject(AppConfig);
    readonly #router = inject(Router);
    readonly #deviceService = inject(DeviceService);
    readonly #modalGestureHelper = inject(ModalGestureHelperService);
    readonly #modalClosingCalculator = inject(ModalGestureIsClosingService);
    readonly #modalGestureAnimationService = inject(ModalGestureAnimationService);
    readonly #modalGestureBackgroundService = inject(ModalGestureBackgroundService);
    readonly #modalGestureTransformOriginService = inject(ModalGestureTransformOriginService);
    readonly #modalGestureQueries = inject(ModalGestureQueriesService);
    readonly #sportsProductUtils = inject(SportsProductUtils);
    readonly #hammerGestureConfig = inject(VERTICAL_GESTURE_CONFIG);

    private get headerHeight(): number | undefined {
        if (!this.#headerSelector) {
            return undefined;
        }

        return (this.#headerHeight ||= this.#modalWindow.querySelector<HTMLElement>(this.#headerSelector)?.clientHeight);
    }

    private get scrollContainer(): HTMLElement | null {
        if (!this.#scrollContainerSelector) {
            return null;
        }

        return (this.#scrollContainer ??= this.#modalWindow.querySelector<HTMLElement>(this.#scrollContainerSelector));
    }

    constructor() {
        if (this.#appConfig.modalDialogGesturesEnabled) {
            this.#destroyRef.onDestroy(() => {
                this.teardown();
            });

            // This code was ported from an earlier version but is probably unnecessary as the service is component-scoped.
            this.#sportsProductUtils
                .fromSportsLeave()
                .pipe(takeUntilDestroyed())
                .subscribe(() => {
                    this.teardown();
                });

            this.#router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
                if (event instanceof NavigationStart) {
                    // The ViewTransitionAnimationGuard prevents background animation conflicts.
                    if (isViewTransitionsEnabled()) {
                        return;
                    }

                    // Cancel background animations on navigation as it can animate the 'wrong' background as the background element remains the same - except overlays.
                    if (!event.url.includes('/event')) {
                        this.#modalGestureHelper.cancelBackgroundAnimations(this.#background);
                    }
                }
            });
        }
    }

    addGestures(modalWindow: unknown, { enabled, headerSelector, scrollTargetQuerySelector }: GestureOptions): void {
        if (!enabled || !(modalWindow instanceof HTMLElement)) {
            return;
        }

        this.initialize(modalWindow, { headerSelector, scrollTargetQuerySelector });
        this.setGestureStyles();
        this.animateSlideInFromBottom();
    }

    private teardown(): void {
        if (this.#modalGestureHelper.isLastVerticalModal()) {
            this.resetBackgroundPan();
        }
    }

    private initialize(modalWindow: HTMLElement, { headerSelector, scrollTargetQuerySelector }: GestureOptions): void {
        this.#modalWindow = modalWindow;
        this.#headerSelector = headerSelector;
        this.#scrollContainerSelector = scrollTargetQuerySelector;
        this.#modalContent = this.#modalGestureQueries.getModalContent();
        this.#background = this.#modalGestureBackgroundService.getBackground();
    }

    private setGestureStyles(): void {
        this.#modalGestureHelper.setBackdropTransparent();

        this.#renderer.setStyle(this.#modalWindow, 'will-change', `transform`);

        this.#background?.elements.forEach((element) => {
            this.#renderer.setStyle(element, 'will-change', `transform, translate, filter`);
        });

        if (this.#background.type === 'sheet') {
            this.#isBackgroundZoomable = false;
        }
    }

    private animateSlideInFromBottom(): void {
        this.#modalGestureAnimationService.slideInFromBottom({ modalWindow: this.#modalWindow, background: this.#background }).finally(() => {
            this.#ngZone.runOutsideAngular(() => {
                this.handlePanEvents(this.#modalWindow);
            });
        });
    }

    private handlePanEvents(panElement: HTMLElement): void {
        const hammer = this.#hammerGestureConfig.buildHammer(panElement);

        this.#destroyRef.onDestroy(() => {
            hammer.destroy?.();
        });

        fromEvent<HammerInput>(hammer, 'panstart')
            .pipe(
                filter(
                    (pan) =>
                        !this.#isDragging &&
                        !this.#isAnimating &&
                        pan.direction === Hammer.DIRECTION_DOWN &&
                        (this.isTouchingHeader(pan.srcEvent) || this.isScrolledToTop()) &&
                        !this.#modalGestureHelper.isExcludedFromGesture(pan.srcEvent.target) &&
                        !this.#modalGestureHelper.isHorizontallyScrollable(pan.srcEvent.target),
                ),
                takeUntilDestroyed(this.#destroyRef),
            )
            .subscribe((pan) => this.handlePanstart(pan));

        fromEvent<HammerInput>(hammer, 'panmove')
            .pipe(
                filter(() => this.#isDragging),
                takeUntilDestroyed(this.#destroyRef),
            )
            .subscribe((pan) => this.handlePanmove(pan));

        merge(
            fromEvent<HammerInput>(hammer, 'panend'),
            fromEvent<TouchEvent>(panElement, 'touchend', { capture: true }).pipe(delay(TRIGGER_PANEND_AFTER_TOUCHEND_MS)),
            fromEvent<TouchEvent>(panElement, 'touchcancel', { capture: true }).pipe(delay(TRIGGER_PANEND_AFTER_TOUCHEND_MS)),
        )
            .pipe(
                filter(() => this.#isDragging),
                takeUntilDestroyed(this.#destroyRef),
            )
            .subscribe((event) => this.handlePanend(event));
    }

    private isTouchingHeader(event: TouchEvent | MouseEvent | PointerEvent): boolean {
        const clientY: number | undefined = (event as TouchEvent)?.touches?.[0]?.clientY ?? (event as MouseEvent)?.clientY; // support non-touch devices for dev

        if (!this.headerHeight) {
            return false;
        }

        return Math.round(clientY) <= this.headerHeight;
    }

    private isScrolledToTop(): boolean {
        if (!this.scrollContainer) {
            return true;
        }

        return Boolean(this.scrollContainer && this.scrollContainer?.scrollTop < 10);
    }

    private handlePanstart(pan: HammerInput): void {
        (this.#document.activeElement as HTMLElement)?.blur();

        this.#isDragging = true;

        // Avoids the forced reflow that would otherwise occur when reading viewport dimensions synchronously.
        // Is a read -> write but won't interleave since it's the only 'measure' callback we queue.
        fastdom.measure(() => {
            this.#modalGestureTransformOriginService.updateTransformOrigin(this.#background);
        });

        fastdom.mutate(() => {
            this.setPanStyles();
        });

        this.handlePanmove(pan);
    }

    private setPanStyles(): void {
        /**
         * This avoids scrolling while panning which occurs at least on Chrome/Android (but not iOS). Also avoids touchmove throttling that occurs with touchmove events
         * on scrollable elements on Android: https://developer.chrome.com/blog/a-more-compatible-smoother-touch#chromes_new_model_the_throttled_async_touchmove_model.
         * Background on Android locked by body overflow: hidden.
         */
        if (!this.#deviceService.isiOS) {
            this.#modalGestureHelper.blockScrolling(this.#modalContent);
            this.#modalGestureHelper.blockScrolling(this.scrollContainer);
        }

        this.#modalGestureHelper.setHeaderBackplateVisible();
        this.#modalGestureHelper.disableBackgroundTransitions(this.#background);
        this.#modalGestureHelper.disableConflictingBackgroundAnimations(this.#background);
        this.#renderer.setStyle(this.#modalWindow, 'animation', `none`);

        if (this.#background?.type === 'event') {
            this.#modalGestureHelper.setEventBackdropOpaque();
        }
    }

    private handlePanmove({ deltaY }: HammerInput): void {
        this.#currentPosition = deltaY - PAN_BUFFER;

        if (!this.#isPanUpdateScheduled) {
            fastdom.mutate(() => {
                this.setPan(this.#currentPosition);

                this.#isPanUpdateScheduled = false;
            });

            this.#isPanUpdateScheduled = true;
        }

        fastdom.mutate(() => {
            if (this.#currentPosition + PAN_BUFFER >= this.#modalGestureHelper.viewportHeight) {
                this.animatePanendClose(this.#currentPosition);
            }
        });
    }

    private setPan(panY = 0): void {
        if (this.#isAnimating) {
            return;
        }

        const panWithinScreen = Math.max(0, Math.min(panY, this.#modalGestureHelper.viewportHeight));

        this.setModalPan(panWithinScreen);
        this.setBackgroundZoom(panWithinScreen);
    }

    private setModalPan(panY = 0): void {
        this.#renderer.setStyle(this.#modalWindow, 'transform', `translate3d(0, ${panY}px, 0)`);
    }

    private setBackgroundZoom(panY: number): void {
        if (!this.#isBackgroundZoomable) {
            return;
        }

        this.#currentZoom = ModalGesturePanUtility.getBackgroundZoomVertical(panY, this.#modalGestureHelper.viewportHeight);
        this.#currentBrightness = ModalGesturePanUtility.getBackgroundBrightnessVertical(panY, this.#modalGestureHelper.viewportHeight);

        this.#background?.elements.forEach((element) => {
            this.#renderer.setStyle(element, 'filter', `brightness(${this.#currentBrightness})`);
            this.#renderer.setStyle(element, 'transform', `scale3d(${this.#currentZoom}, ${this.#currentZoom}, 1)`);
        });
    }

    private handlePanend(pan: HammerInput | TouchEvent): void {
        const { deltaY, velocity } = this.#modalGestureHelper.getHammerInput(pan, 'vertical');

        if (!this.#isDragging) {
            return;
        }

        this.#isDragging = false;

        const currentPosition = deltaY - PAN_BUFFER;

        if (currentPosition <= 0) {
            fastdom.mutate(() => {
                this.resetPanStyles();
            });

            return;
        }

        fastdom.mutate(() => {
            this.animatePanend(currentPosition, velocity);
        });
    }

    private resetPanStyles(): void {
        this.resetModalPan();
        this.resetBackgroundPan();
    }

    private resetModalPan(): void {
        this.#renderer.setStyle(this.#modalWindow, 'animation', `none`);
        this.#renderer.setStyle(this.#modalWindow, 'transform', 'translate3d(0, 0px, 0)');

        if (!this.#deviceService.isiOS) {
            this.#modalGestureHelper.unblockScrolling(this.#modalContent);
            this.#modalGestureHelper.unblockScrolling(this.scrollContainer);
        }
    }

    private resetBackgroundPan(): void {
        this.#background?.elements.forEach((element) => {
            this.#renderer.removeStyle(element, 'filter');
            this.#renderer.removeStyle(element, 'transform');
        });

        this.#modalGestureHelper.setHeaderBackplateTransparent();
        this.#modalGestureHelper.enableBackgroundTransitions(this.#background);
        this.#modalGestureHelper.enableConflictingBackgroundAnimations(this.#background);
    }

    private animatePanend(deltaY: number, velocity: number): void {
        if (this.#modalClosingCalculator.isModalClosingVertical(deltaY, velocity)) {
            this.animatePanendClose(deltaY);
        } else {
            this.animatePanendReopen(deltaY);
        }
    }

    private animatePanendClose(deltaY: number): void {
        this.#isAnimating = true;

        this.#modalGestureAnimationService
            .slideModalVertical(
                {
                    from: `${deltaY}px`,
                    to: `${this.#modalGestureHelper.viewportHeight}px`,
                    duration: PANEND_CLOSE_DURATION_VERTICAL,
                    easing: PANEND_CLOSE_EASING_VERTICAL,
                },
                this.#modalWindow,
            )
            ?.finally(() => {
                this.#isAnimating = false;

                this.close();
            });

        this.#modalGestureAnimationService
            .zoomBackground(
                {
                    from: `${this.#currentZoom}`,
                    to: `${MAX_BACKGROUND_SCALE}`,
                    fromBrightness: `${this.#currentBrightness}`,
                    toBrightness: `${MAX_BACKGROUND_BRIGHTNESS}`,
                    duration: PANEND_CLOSE_DURATION_VERTICAL,
                    easing: PANEND_CLOSE_EASING_VERTICAL,
                },
                { isClosing: true, updateTransformOrigin: false },
                this.#background,
            )
            ?.finally(() => {
                this.resetBackgroundPan();
            });

        if (this.#modalGestureHelper.isLastVerticalModal()) {
            this.removeSgpClasses();
        }
    }

    private animatePanendReopen(deltaY: number): void {
        this.#isAnimating = true;

        this.#modalGestureAnimationService
            .slideModalVertical(
                {
                    from: `${deltaY}px`,
                    to: `0px`,
                    duration: PANEND_REOPEN_DURATION_VERTICAL,
                    easing: PANEND_REOPEN_EASING_VERTICAL,
                },
                this.#modalWindow,
            )
            ?.finally(() => {
                this.#isAnimating = false;

                this.resetModalPan();
            });

        this.#modalGestureAnimationService
            .zoomBackground(
                {
                    from: `${this.#currentZoom}`,
                    to: `${MIN_BACKGROUND_SCALE}`,
                    fromBrightness: `${this.#currentBrightness}`,
                    toBrightness: `${MIN_BACKGROUND_BRIGHTNESS}`,
                    duration: PANEND_REOPEN_DURATION_VERTICAL,
                    easing: PANEND_REOPEN_EASING_VERTICAL,
                },
                { isClosing: false, updateTransformOrigin: false },
                this.#background,
            )
            ?.finally(() => {
                this.resetBackgroundPan();
            });
    }

    private removeSgpClasses(): void {
        this.#renderer.removeClass(this.#document.documentElement, 'sheet-view-for-market');
        this.#renderer.removeClass(this.#document.documentElement, 'sheet-view-for-market-for-sgp');
    }

    private close(): void {
        this.#modalGestureHelper.getActiveModal()?.close({ closeAction: PopupAction.CloseByPan });
    }
}
